import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from './cn';
import { IconButton } from './IconButton';

/** Root. Same API as `Dialog`; the panel slides in from the right. */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

const SIZES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-xl',
} as const;

export interface SheetContentProps extends Omit<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
  'title'
> {
  title: ReactNode;
  description: ReactNode;
  hideDescription?: boolean;
  size?: keyof typeof SIZES;
  closeLabel?: string;
}

/**
 * Side panel for secondary detail (help, pair details). Full width on
 * phones. Compose with `SheetBody` (scrolls) and an optional `SheetFooter`.
 */
export const SheetContent = /* @__PURE__ */ forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(function SheetContent(
  {
    title,
    description,
    hideDescription = false,
    size = 'md',
    closeLabel = 'Close',
    className,
    children,
    ...props
  },
  ref
) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-overlay/40 data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col border-l border-border bg-surface-raised text-fg shadow-lg outline-none data-[state=closed]:animate-slide-out-right data-[state=open]:animate-slide-in-right',
          SIZES[size],
          className
        )}
        {...props}
      >
        <div className="flex items-start gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1 space-y-1">
            <DialogPrimitive.Title className="text-base font-semibold leading-6 text-fg">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description
              className={cn(
                hideDescription ? 'sr-only' : 'text-sm text-fg-muted'
              )}
            >
              {description}
            </DialogPrimitive.Description>
          </div>
          <DialogPrimitive.Close asChild>
            <IconButton
              aria-label={closeLabel}
              tooltip={false}
              icon={<X />}
              size="sm"
              className="-mr-2 -mt-0.5"
            />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

/** Scrolling content region of a sheet. */
export const SheetBody = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'min-h-0 flex-1 overflow-y-auto px-5 py-4 text-sm',
      className
    )}
    {...props}
  />
);

export const SheetFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 border-t border-border bg-surface-sunken px-5 py-3 sm:flex-row sm:items-center sm:justify-end',
      className
    )}
    {...props}
  />
);
